import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from 'antd';
import {
  WaveForm,
  Ruler,
  PreviewAudioPlayer,
  Controls,
  Cursor,
  AdsMarkerTable,
  Silences,
  AdMarker,
} from '../ui/admarker';
import { getDefaultStarts } from '../../utils/adsMarker';
import useEventListener from '../hooks/useEventListener';

const PADDING_WIDTH = 0;

const MarkerPlacer = ({
  enableMonetization,
  isVisible,
  encoderData = {},
  onChange,
  value: markers,
}) => {
  const {
    duration: episodeDuration,
    url: episodeAudio,
    silenceDetected: episodeSilences,
  } = encoderData;

  const [zoomLevel, setZoomLevel] = useState(1);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(0);
  const [secondsPerPixel, setSecondsPerPixel] = useState(0);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  // player
  const [isAudioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [isPlayingAd, setPlayingAd] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [seek, setSeek] = useState(0);
  const [audioElement, setAudioElement] = useState();

  const containerRef = useRef();

  useEffect(() => {
    const setWidth = () => {
      if (episodeDuration && containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const cW = Math.max(width - PADDING_WIDTH * 2, 0);
        const tW = cW * zoomLevel;
        setTimelineWidth(tW);
        setPixelsPerSecond(episodeDuration ? tW / episodeDuration : tW);
        setSecondsPerPixel(episodeDuration / tW);
      }
    };

    if (isVisible && enableMonetization) {
      setWidth();
    }
  }, [zoomLevel, episodeDuration, isVisible, enableMonetization]);

  const handleSeek = useCallback(
    (event) => {
      const ignoreMe = Boolean(event.target.closest('.ignoreSeekClick'));
      if (!selectedMarkerId && !ignoreMe) {
        const rect = event.target.getBoundingClientRect();
        const xPos = event.clientX - rect.left;
        const scrollLeft = containerRef.scrollLeft || 0;
        const res = (xPos + scrollLeft) * secondsPerPixel;
        setSeek(res);
      }
    },
    [secondsPerPixel, selectedMarkerId]
  );

  useEventListener('click', handleSeek, containerRef.current);

  const handleMarkerChange = useCallback(
    (markerPlacement, start) => {
      const newMarkers = markers.map((marker) => {
        if (marker.placement === markerPlacement) {
          return { ...marker, start };
        }
        return marker;
      });
      onChange(newMarkers);
    },
    [markers, onChange]
  );

  const handleAddMarker = useCallback(
    (placement) => {
      const newMarkers = markers.concat([
        {
          placement,
          start: getDefaultStarts({
            episodeDuration,
            placement,
            silences: episodeSilences,
            markers,
          }),
        },
      ]);
      onChange(newMarkers);
    },
    [episodeDuration, markers, onChange, episodeSilences]
  );

  const handleRemoveMarker = useCallback(
    (placement) => {
      const newMarkers = markers.filter((marker) => marker.placement !== placement);
      onChange(newMarkers);
    },
    [markers, onChange]
  );

  return enableMonetization ? (
    <>
      <Divider />

      <PreviewAudioPlayer
        src={episodeAudio}
        adMarkers={markers}
        isPlaying={isPlaying}
        setPlaying={setPlaying}
        setPlayingAd={setPlayingAd}
        setAudioLoading={setAudioLoading}
        setCurrentTime={setCurrentTime}
        currentTime={currentTime}
        seek={seek}
        setAudioElement={setAudioElement}
      />
      {isVisible && (
        <div ref={containerRef} className="editor-timeline-wrapper">
          <div
            className="editor-timeline"
            style={{
              width: timelineWidth,
            }}
          >
            <div className="editor-timeline-waveform-container">
              <WaveForm
                width={timelineWidth}
                episodeDuration={episodeDuration}
                audioUrl={episodeAudio}
                currentTime={currentTime}
              />
            </div>

            <Ruler episodeDuration={episodeDuration} width={timelineWidth} />

            {episodeSilences && (
              <Silences silenceDetected={episodeSilences} pixelsPerSecond={pixelsPerSecond} />
            )}

            <div className="editor-timeline__markers">
              {markers.map((marker, i) => (
                <AdMarker
                  markers={markers}
                  episodeDuration={episodeDuration}
                  pixelsPerSecond={pixelsPerSecond}
                  silenceDetected={episodeSilences || []}
                  handleMarkerChange={handleMarkerChange}
                  marker={marker}
                  key={i}
                  setSelectedMarkerId={setSelectedMarkerId}
                  selectedMarkerId={selectedMarkerId}
                />
              ))}
            </div>

            <Cursor currentTime={currentTime} pixelsPerSecond={pixelsPerSecond} />
          </div>
        </div>
      )}

      <Controls
        episodeDuration={episodeDuration}
        isPlaying={isPlaying}
        isPlayingAd={isPlayingAd}
        isAudioLoading={isAudioLoading}
        currentTime={currentTime}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        audioElement={audioElement}
      />

      <AdsMarkerTable
        markers={markers}
        silenceDetected={episodeSilences}
        handleMarkerChange={handleMarkerChange}
        episodeDuration={episodeDuration}
        handleAddMarker={handleAddMarker}
        handleRemoveMarker={handleRemoveMarker}
        setSeek={setSeek}
      />
    </>
  ) : null;
};

MarkerPlacer.propTypes = {
  enableMonetization: PropTypes.bool,
  value: PropTypes.array,
  onChange: PropTypes.func,
  isVisible: PropTypes.bool,
  encoderData: PropTypes.object,
};

export default MarkerPlacer;
